import * as React from 'react'
import {
    Alert,
    Box,
    Checkbox,
    Chip,
    CircularProgress,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import CompactAssetIcon from './CompactAssetIcon'

const PLUGIN_KEY = 'technicus'
const EMPTY_CONFIG = Object.freeze({})

const cloneValue = value => typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value ?? {}))
const digits = value => String(value ?? '').replace(/[^\d]/g, '')
const bounded = (value, fallback, minimum, maximum) => {
    const parsed = Number.parseInt(String(value ?? ''), 10)
    return String(Number.isFinite(parsed) ? Math.min(maximum, Math.max(minimum, parsed)) : fallback)
}

export const normalizeTechnicusSettings = pluginConfig => ({
    upgradeInventory: pluginConfig?.upgradeInventory === true,
    upgradeCommanders: pluginConfig?.upgradeCommanders === true,
    upgradeCastellans: pluginConfig?.upgradeCastellans === true,
    targetLevel: bounded(pluginConfig?.targetLevel, 20, 1, 20),
    attemptsPerCycle: bounded(pluginConfig?.attemptsPerCycle, 25, 1, 250),
    intervalSeconds: bounded(pluginConfig?.intervalSeconds, 600, 60, 86400),
    selectedEquipment: pluginConfig?.selectedEquipment && typeof pluginConfig.selectedEquipment === 'object' ?
        Object.fromEntries(Object.entries(pluginConfig.selectedEquipment).filter(([, selected]) => selected === true)) : {}
})

const TARGETS = Object.freeze([
    ['upgradeInventory', 'inventory', 'Inventory equipment', 'Improve eligible normal equipment stored in the inventory.'],
    ['upgradeCommanders', 'commanders', 'Commander equipment', 'Improve eligible normal equipment currently worn by commanders.'],
    ['upgradeCastellans', 'castellans', 'Castellan equipment', 'Improve eligible normal equipment currently worn by castellans.']
])
const SETTING_BY_LOCATION = Object.freeze({
    inventory: 'upgradeInventory',
    commanders: 'upgradeCommanders',
    castellans: 'upgradeCastellans'
})

const EquipmentCard = ({ item, selected, locationEnabled, onToggle }) => (
    <Paper
        variant="outlined"
        sx={{
            p: 1.4,
            height: '100%',
            borderRadius: '15px',
            borderColor: selected ? item.rarityColor : 'rgba(155,180,216,.18)',
            bgcolor: selected ? 'rgba(45,68,103,.55)' : 'rgba(12,23,41,.54)',
            opacity: item.canUpgrade ? 1 : .64
        }}
    >
        <Stack direction="row" spacing={1.1} alignItems="center">
            <Checkbox
                checked={selected}
                disabled={!item.canUpgrade}
                onChange={event => onToggle(event.target.checked)}
                inputProps={{ 'aria-label': `Select ${item.label}` }}
            />
            <Box sx={{ p: .45, borderRadius: '11px', border: `2px solid ${item.rarityColor || '#8ea0b8'}`, bgcolor: 'rgba(0,0,0,.2)' }}>
                <CompactAssetIcon iconUrl={item.iconUrl} itemType="resource" size={55} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography fontWeight={850} noWrap title={item.label}>{item.label}</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                    {item.rarityLabel} · {item.slotLabel} · {item.locationLabel}{item.lordName ? `: ${item.lordName}` : ''}
                </Typography>
                <Stack direction="row" spacing={.7} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: .65 }}>
                    <Chip size="small" label={`Level ${item.enchantmentLevel}/${item.maximumLevel}`} />
                    {!item.canUpgrade ? <Chip size="small" color="success" label="Target reached" /> : null}
                    {selected && !locationEnabled ? <Chip size="small" color="warning" label="Location disabled" /> : null}
                </Stack>
            </Box>
        </Stack>
    </Paper>
)

export default function TechnicusSettingsPanel({ userPlugins, onChange, runtimeStatus }) {
    const pluginConfig = React.useMemo(() => userPlugins?.[PLUGIN_KEY] ?? EMPTY_CONFIG, [userPlugins])
    const settings = React.useMemo(() => normalizeTechnicusSettings(pluginConfig), [pluginConfig])
    const [search, setSearch] = React.useState('')
    const [locationFilter, setLocationFilter] = React.useState('all')
    const equipment = React.useMemo(() => Array.isArray(runtimeStatus?.equipment) ? runtimeStatus.equipment : [], [runtimeStatus?.equipment])
    const visibleEquipment = React.useMemo(() => {
        const query = search.trim().toLowerCase()
        return equipment.filter(item =>
            (locationFilter === 'all' || item.location === locationFilter) &&
            (!query || `${item.label} ${item.rarityLabel} ${item.slotLabel} ${item.locationLabel} ${item.lordName || ''}`.toLowerCase().includes(query))
        )
    }, [equipment, locationFilter, search])
    const selectedCount = React.useMemo(() => Object.values(settings.selectedEquipment).filter(Boolean).length, [settings.selectedEquipment])

    const updateSettings = React.useCallback(mutator => {
        const nextSettings = cloneValue(settings)
        mutator(nextSettings)
        const nextPlugins = cloneValue(userPlugins ?? {})
        nextPlugins[PLUGIN_KEY] ??= {}
        Object.assign(nextPlugins[PLUGIN_KEY], normalizeTechnicusSettings(nextSettings))
        onChange(nextPlugins)
    }, [onChange, settings, userPlugins])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '22px', bgcolor: 'rgba(18,29,48,.82)' }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <AutoFixHighRoundedIcon sx={{ color: '#ffc857' }} />
                    <Box>
                        <Typography variant="h5" fontWeight={900}>Technicus</Typography>
                        <Typography color="text.secondary">Choose every equipment location that Technicus may improve.</Typography>
                    </Box>
                </Stack>
            </Paper>

            <Grid container spacing={1.5}>
                {TARGETS.map(([key, location, label, description]) => (
                    <Grid item xs={12} md={4} key={key}>
                        <Paper sx={{ p: 2, height: '100%', borderRadius: '17px', bgcolor: 'rgba(20,31,52,.72)' }}>
                            <Stack direction="row" spacing={1.1} alignItems="flex-start">
                                <Switch checked={settings[key]} onChange={event => updateSettings(next => { next[key] = event.target.checked })} />
                                <Box>
                                    <Typography fontWeight={850}>{label}</Typography>
                                    <Typography variant="body2" color="text.secondary">{description}</Typography>
                                    <Typography variant="caption" color="primary.light">{runtimeStatus?.counts?.[location] ?? 0} found live</Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Paper sx={{ p: 2, borderRadius: '18px', bgcolor: 'rgba(18,29,48,.72)' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Target enchantment level"
                            value={settings.targetLevel}
                            onChange={event => updateSettings(next => { next.targetLevel = digits(event.target.value) })}
                            onBlur={() => updateSettings(next => { next.targetLevel = bounded(next.targetLevel, 20, 1, 20) })}
                            helperText="Automatically limited by the item's rarity."
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Maximum attempts per cycle"
                            value={settings.attemptsPerCycle}
                            onChange={event => updateSettings(next => { next.attemptsPerCycle = digits(event.target.value) })}
                            onBlur={() => updateSettings(next => { next.attemptsPerCycle = bounded(next.attemptsPerCycle, 25, 1, 250) })}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Cycle interval (seconds)"
                            value={settings.intervalSeconds}
                            onChange={event => updateSettings(next => { next.intervalSeconds = digits(event.target.value) })}
                            onBlur={() => updateSettings(next => { next.intervalSeconds = bounded(next.intervalSeconds, 600, 60, 86400) })}
                            helperText="Minimum 60 seconds."
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: { xs: 1.5, md: 2 }, borderRadius: '18px', bgcolor: 'rgba(18,29,48,.72)' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6" fontWeight={900}>Live equipment selection</Typography>
                            {runtimeStatus?.busy ? <CircularProgress size={17} /> : null}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            {equipment.length} eligible item(s) found · {selectedCount} selected
                        </Typography>
                    </Box>
                    <TextField
                        size="small"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        placeholder="Search equipment"
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
                        sx={{ width: { xs: '100%', md: 300 } }}
                    />
                </Stack>

                <Stack direction="row" spacing={.8} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                    <Chip clickable color={locationFilter === 'all' ? 'primary' : 'default'} label={`All (${equipment.length})`} onClick={() => setLocationFilter('all')} />
                    {TARGETS.map(([, location, label]) => (
                        <Chip
                            key={location}
                            clickable
                            color={locationFilter === location ? 'primary' : 'default'}
                            label={`${label.replace(' equipment', '')} (${runtimeStatus?.counts?.[location] ?? 0})`}
                            onClick={() => setLocationFilter(location)}
                        />
                    ))}
                </Stack>

                {visibleEquipment.length > 0 ? (
                    <Grid container spacing={1.2}>
                        {visibleEquipment.map(item => {
                            const inventoryID = String(item.inventoryID)
                            const locationSetting = SETTING_BY_LOCATION[item.location]
                            return (
                                <Grid item xs={12} lg={6} key={`${item.location}-${inventoryID}`}>
                                    <EquipmentCard
                                        item={item}
                                        selected={settings.selectedEquipment[inventoryID] === true}
                                        locationEnabled={settings[locationSetting] === true}
                                        onToggle={checked => updateSettings(next => {
                                            next.selectedEquipment ??= {}
                                            if (checked) {
                                                next.selectedEquipment[inventoryID] = true
                                                if (locationSetting)
                                                    next[locationSetting] = true
                                            }
                                            else
                                                delete next.selectedEquipment[inventoryID]
                                        })}
                                    />
                                </Grid>
                            )
                        })}
                    </Grid>
                ) : (
                    <Alert severity={runtimeStatus?.message?.includes('failed') ? 'error' : 'info'}>
                        {runtimeStatus?.message || 'Waiting for the bot to load the live equipment list. The first scan runs shortly after the account connects.'}
                    </Alert>
                )}
                {visibleEquipment.length > 0 && runtimeStatus?.message ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.4 }}>{runtimeStatus.message}</Typography>
                ) : null}
            </Paper>

            <Alert severity="info">Technicus uses coins only. Relic equipment and non-enchantable slots are skipped automatically; rubies are never sent in the upgrade request.</Alert>
        </Box>
    )
}
